import { Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { NavigationError, Router } from '@angular/router';
import { AtsNotificationService, Notice, NoticeType } from '@ats/notifications';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CmModalSettings } from '../../models';
import { CmLoadingDialogComponent } from '../cm-loading-dialog/cm-loading-dialog.component';
import { CmSnackBarA11yComponent } from './sm-snackbar-a11y/cm-snackbar-a11y.component';

@UntilDestroy()
@Component({
	selector: 'cm-notifications',
	templateUrl: './cm-notifications.component.html',
	styleUrls: ['./cm-notifications.component.scss'],
	standalone: false,
})
export class CmNotificationsComponent {
	loading$: Observable<boolean>;
	config: MatSnackBarConfig = {
		duration: 5000,
		horizontalPosition: 'right',
		verticalPosition: 'top',
		politeness: 'off',
	};
	private dialogRef: MatDialogRef<
		CmLoadingDialogComponent,
		{ key: string; defaultValue: string }
	> = null;

	constructor(
		private readonly notifications: AtsNotificationService,
		private readonly snackBar: MatSnackBar,
		private readonly router: Router,
		private dialog: MatDialog
	) {
		this.loading$ = this.notifications.loading$;
		this.notifications.notice$
			.pipe(untilDestroyed(this))
			.subscribe(notice => this.popNotification(notice));

		this.router.events
			.pipe(
				filter(e => e instanceof NavigationError),
				untilDestroyed(this)
			)
			.subscribe(e =>
				this.popNotification({
					id: 'routing',
					message: e.toString(),
					type: NoticeType.ERROR,
				})
			);

		this.notifications.loaderMessage$.pipe(untilDestroyed(this)).subscribe(message => {
			if (message) {
				this.popLoader(message);
			} else {
				this.hideLoader();
			}
		});
	}

	private popLoader(text: { key: string; defaultValue: string }) {
		this.dialogRef = this.dialog.open<
			CmLoadingDialogComponent,
			{ key: string; defaultValue: string }
		>(CmLoadingDialogComponent, {
			...CmModalSettings.Medium,
			data: text,
			disableClose: true,
		});
	}

	private hideLoader() {
		if (this.dialogRef) {
			this.dialogRef.close();
			this.dialogRef = null;
		}
	}

	private popNotification(notice: Notice) {
		let panelClass: string;

		switch (notice.type) {
			case NoticeType.SUCCESS:
				panelClass = 'success-snackbar';
				break;
			case NoticeType.NEUTRAL:
				panelClass = 'neutral-snackbar';
				break;
			case NoticeType.ERROR:
				panelClass = 'error-snackbar';
				break;
			default:
				panelClass = 'info-snackbar';
				break;
		}

		this.snackBar.openFromComponent(CmSnackBarA11yComponent, {
			...this.config,
			panelClass,
			data: { message: notice.message, action: notice.action },
		});
	}
}
